import { Outlet, Link, createRootRoute, HeadContent, Scripts, useRouterState } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { SmoothScroll } from "@/components/SmoothScroll";
import { BackToTop } from "@/components/BackToTop";
import { AccessibilityWidget } from "@/components/AccessibilityWidget";
import { ScrollFadeSections } from "@/components/ScrollFadeSections";
import appCss from "../styles.css?url";
import socialBanner from "@/assets/mckay-social-banner.png.asset.json";

// Runs before hydration on every full page load: mirrors applyPrefs() in
// useA11yPrefs so accessibility settings persist across pages and refreshes.
const A11Y_BOOT_SCRIPT = `(function(){try{var p=JSON.parse(localStorage.getItem("a11y-prefs-v1")||"{}");var r=document.documentElement;r.setAttribute("data-a11y-text-size",p.textSize||"A");var m={"a11y-high-contrast":!!p.highContrast,"a11y-readable-font":!!p.readableFont,"a11y-underline-links":!!p.underlineLinks,"a11y-larger-cursor":!!p.largerCursor,"a11y-reduce-motion":!!(p.reduceMotion||p.pauseMedia),"a11y-pause-media":!!p.pauseMedia};for(var k in m){r.classList.toggle(k,m[k]);}}catch(e){}})();`;

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "McKay Roofing & Solar – Roofing & Solar in Southern California" },
      { name: "description", content: "Family-owned roofing and solar since 1981. Repairs, replacements, and solar installs across Carlsbad and all of Southern California, CA. Free estimates." },
      { name: "author", content: "McKay Roofing & Solar" },
      { name: "publisher", content: "McKay Roofing & Solar" },
      { name: "copyright", content: "McKay Roofing & Solar" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { name: "googlebot", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      { name: "bingbot", content: "index, follow" },
      { httpEquiv: "X-Robots-Tag", content: "index, follow, max-image-preview:large" },
      { name: "keywords", content: "roofing Southern California, roof repair Southern California, roof replacement Carlsbad, solar installation Southern California, solar panels California, residential roofing, commercial roofing, Tesla Powerwall, McKay Roofing & Solar" },
      { name: "geo.region", content: "US-CA" },
      { name: "geo.placename", content: "Carlsbad" },
      { property: "og:site_name", content: "McKay Roofing & Solar" },
      { property: "og:locale", content: "en_US" },
      { property: "og:title", content: "McKay Roofing & Solar – Roofing & Solar in Southern California" },
      { property: "og:description", content: "Family-owned roofing and solar since 1981, serving Carlsbad and all of Southern California, CA." },
      { property: "og:image", content: `https://mckay.steadyautogrowthdemo.com${socialBanner.url}` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "McKay Roofing & Solar – Roofing & Solar in Southern California" },
      { name: "twitter:description", content: "Family-owned roofing and solar since 1981, serving all of Southern California, CA." },
      { name: "twitter:image", content: `https://mckay.steadyautogrowthdemo.com${socialBanner.url}` },
      
    ],
    links: [
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Montserrat:wght@600;700;800;900&family=Inter:wght@400;500;600;700&display=swap",
      },
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
    scripts: [


      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "RoofingContractor",
          "@id": "https://mckay.steadyautogrowthdemo.com/#business",
          name: "McKay Roofing & Solar",
          image: `https://mckay.steadyautogrowthdemo.com${socialBanner.url}`,
          url: "https://mckay.steadyautogrowthdemo.com",
          telephone: "+1-858-541-1097",
          email: "sales@mckayroofingsolar.com",
          priceRange: "$$",
          address: {
            "@type": "PostalAddress",
            streetAddress: "9520 Padgett St, Suite 107",
            addressLocality: "San Diego",
            addressRegion: "CA",
            postalCode: "92126",
            addressCountry: "US",
          },
          areaServed: [
            { "@type": "AdministrativeArea", name: "Southern California" },
            { "@type": "City", name: "Carlsbad" },
            { "@type": "City", name: "Pine Valley" },
            { "@type": "City", name: "Winter Gardens" },
            { "@type": "City", name: "Southern California" },
            { "@type": "City", name: "Coronado" },
          ],
          openingHoursSpecification: [{
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
            opens: "07:00",
            closes: "19:00",
          }],
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.9",
            reviewCount: "150",
          },
          sameAs: [],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        {/* Apply saved accessibility preferences before first paint so they
            survive full page loads and never flash the default styling. */}
        <script dangerouslySetInnerHTML={{ __html: A11Y_BOOT_SCRIPT }} />
      </head>
      <body>

        {children}
        <Scripts />
      </body>
    </html>
  );
}


function RootComponent() {
  const [queryClient] = useState(() => new QueryClient());
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const prevPathRef = useRef<string | null>(null);
  const isPopRef = useRef(false);

  // Track back/forward navigations so we can distinguish them from
  // forward pushes that should scroll to top.
  useEffect(() => {
    const onPop = () => {
      isPopRef.current = true;
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Safety net: on every route change, remove any leftover transition
  // overlays and restore scroll. Prevents "can't scroll" when an
  // explore/loading overlay is orphaned across navigations.
  useEffect(() => {
    const prev = prevPathRef.current;
    const isNewRoute = prev !== null && prev !== pathname;
    const isPop = isPopRef.current;
    isPopRef.current = false;
    prevPathRef.current = pathname;

    // Skip overlays still mid-animation (component removes them itself).
    document
      .querySelectorAll(".mck-explore-portal:not([data-active='1'])")
      .forEach((el) => el.remove());
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
    // Re-sync smooth scroller – a stale Lenis state after aborted
    // transitions or dynamic content can leave the page unscrollable.
    const lenis = (typeof window !== "undefined" ? window.__lenis : null) as
      | {
          start: () => void;
          resize: () => void;
          scrollTo: (target: number, opts?: { immediate?: boolean; force?: boolean }) => void;
        }
      | null
      | undefined;

    // Forward navigation to a new route: always start at the top instantly.
    // Back/forward (popstate): let the browser's restored scroll stand.
    if (isNewRoute && !isPop) {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } catch {}
    }

    if (lenis) {
      try {
        lenis.start();
        lenis.resize();
        // Browser restores scrollY on back/forward nav, but Lenis's internal
        // target is stale – sync it so the next wheel/touch doesn't snap.
        // For forward nav, force an immediate jump to 0 so there's no animated scroll.
        lenis.scrollTo(isNewRoute && !isPop ? 0 : window.scrollY, { immediate: true, force: true });
      } catch {}
    }
    // Second pass after layout settles (fonts, images, reveal transitions).
    const t = window.setTimeout(() => {
      try {
        lenis?.resize();
        lenis?.scrollTo(window.scrollY, { immediate: true, force: true });
      } catch {}
    }, 250);
    return () => window.clearTimeout(t);
  }, [pathname]);

  return (
    <QueryClientProvider client={queryClient}>
      <SmoothScroll>
        <Outlet />
        <ScrollFadeSections />
        <BackToTop />
        <AccessibilityWidget />
      </SmoothScroll>
    </QueryClientProvider>
  );
}
